import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormGroup, Stack, Typography } from '@mui/material';
import Button from '../../components/styledComponents/Button';
import SortableTable from './SortableTable';
import { useAuth } from '../../auth/AuthContext';
import { useGetForms } from '../../services/getForms';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useGetPublog } from '../../services/getPublog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment, { Moment } from 'moment';
import { groupPublogRecords, notify, transformToForms, userHasAccess } from '../../utils/helper';
import { useQueryClient } from '@tanstack/react-query';
import { deleteForm, publishForm } from '../../api';
import Dialog from '../../components/styledComponents/Dialog';
import { GridFilterModel, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { accessPolicy, UserRoles } from '../../constants';

export type Form = {
  formNumber: string;
  formTitle: string;
  status: string;
  dateModified: string;
  id: string;
  user: {
    email: string;
  };
  userEmail: string;
};

export type FormListType = 'UNPUBLISHED' | 'PUBLISHED';

type BulkAction = 'PUBLISH' | 'DELETE';

function FormList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formListType, setFormListType] = useState<FormListType>('UNPUBLISHED');
  const [endDate, setEndDate] = useState<Moment>(moment());
  const [selectedFormIds, setSelectedFormIds] = React.useState<GridRowSelectionModel>([]);
  const [bulkActionInProgress, setBulkActionInProgress] = useState<boolean>(false);
  const [bulkActionDialog, setBulkActionDialog] = useState<BulkAction | undefined>(undefined);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const activeFilter = filterModel.items[0]?.value ? filterModel.items[0] : null;

  const userRoles = user?.groups as UserRoles[];

  const selectedFormCount = selectedFormIds.length;

  const formsRQ = useGetForms(
    selectedFormIds,
    setSelectedFormIds,
    bulkActionInProgress,
    setBulkActionInProgress,
    activeFilter
  );

  const publogRQ = useGetPublog(endDate.format('YYYY-MM-DD'));
  const activeRQ = formListType === 'PUBLISHED' ? publogRQ : formsRQ;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = activeRQ;
  const { bulkTimedOutIds, setBulkTimedOutIds } = formsRQ;

  const isDelete = bulkActionDialog === 'DELETE';
  const verb = isDelete ? 'delete' : 'publish';

  const noun = selectedFormCount === 1 ? 'this form' : `${selectedFormCount} forms`;

  const warning = isDelete
    ? 'This action cannot be undone.'
    : 'This will publish all of the selected forms.';

  const confirmLabel = `Yes, ${verb} ${noun}`;

  const handleBulkAction = async () => {
    setBulkTimedOutIds(null);
    setBulkActionInProgress(true);

    const BATCH_SIZE = 10;

    for (let i = 0; i < selectedFormIds.length; i += BATCH_SIZE) {
      const chunk = selectedFormIds.slice(i, i + BATCH_SIZE);

      await Promise.allSettled(
        chunk.map((composite) => {
          const id = composite.toString().split('#')[0];
          switch (bulkActionDialog) {
            case 'PUBLISH':
              return publishForm(id);
            case 'DELETE':
              return deleteForm(id);
            default:
              return Promise.resolve();
          }
        })
      );
    }

    setBulkActionDialog(undefined);
    queryClient.invalidateQueries({ queryKey: ['forms'] });
  };

  const forms = useMemo(() => {
    if (formListType === 'UNPUBLISHED' && data) {
      return data.pages.flatMap((page: any) => page.forms);
    }

    if (formListType === 'PUBLISHED' && data) {
      const allRecords = data.pages.flatMap((page: any) => page.result);
      const recordsGroupedByPublishEvent = groupPublogRecords(allRecords);
      // create array of forms using first publog record at each timestamp
      const forms = transformToForms(
        Object.keys(recordsGroupedByPublishEvent).map((key) => recordsGroupedByPublishEvent[key][0])
      );

      return forms;
    }

    return [];
  }, [formListType, data]);

  //fetch next page if more forms are needed than had
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const { page, pageSize } = paginationModel;
    const needed = (page + 2) * pageSize;

    if (forms.length < needed) {
      fetchNextPage();
    }
  }, [paginationModel, forms.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  //error notification for failed forms
  useEffect(() => {
    if (!bulkTimedOutIds) return;
    notify(
      `${verb} failed for ${bulkTimedOutIds
        .map((id) => forms.find((f) => f.id === id)?.formNumber)
        .join(', ')}`,
      'error',
      10_000
    );
  }, [bulkTimedOutIds, forms, verb]);

  useEffect(() => {
    if (bulkActionInProgress)
      notify('Bulk Action in progress, please do not navigate away', 'warn');
  }, [bulkActionInProgress]);

  return (
    <Box className="wrapper container">
      <Box className="content-box">
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 178px)' }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between">
              <Box>
                <h2 className="page-title margin-bottom-0">Forms</h2>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate('/forms/add')}
                sx={{ maxHeight: '45px' }}
              >
                Create Form
              </Button>
            </Stack>
            <Stack
              component={FormGroup}
              direction={'row'}
              justifyContent="space-between"
              alignItems="center"
              width={'100%'}
              minHeight={60}
            >
              <ToggleButtonGroup
                color="primary"
                size="small"
                value={formListType}
                exclusive
                onChange={(_e, newValue: FormListType | null) =>
                  setFormListType(newValue ?? 'UNPUBLISHED')
                }
              >
                <ToggleButton value="UNPUBLISHED">UNPUBLISHED</ToggleButton>
                <ToggleButton value="PUBLISHED" aria-label="centered">
                  PUBLISHED
                </ToggleButton>
              </ToggleButtonGroup>

              {formListType === 'UNPUBLISHED' && (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography
                      fontWeight={500}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: 14,
                        width: 140,
                        textAlign: 'center',
                      }}
                    >
                      {selectedFormCount} form{selectedFormCount === 1 ? '' : 's'} selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose a bulk action to apply to the selected form(s)
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    {userHasAccess(accessPolicy.formPublish.read, userRoles) && (
                      <Button
                        variant="contained"
                        size="small"
                        loading={bulkActionInProgress}
                        disabled={bulkActionInProgress || !selectedFormCount}
                        onClick={() => setBulkActionDialog('PUBLISH')}
                      >
                        Publish
                      </Button>
                    )}
                    {userHasAccess(accessPolicy.form._delete, userRoles) && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        loading={bulkActionInProgress}
                        disabled={bulkActionInProgress || !selectedFormCount}
                        onClick={() => setBulkActionDialog('DELETE')}
                      >
                        Delete
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}

              {formListType === 'PUBLISHED' && (
                <Stack direction="row" justifyContent={'flex-end'} sx={{ width: 0.48 }}>
                  <DatePicker
                    sx={{ width: '50%' }}
                    label="End Date"
                    slotProps={{ textField: { size: 'small' } }}
                    value={endDate}
                    onChange={(newValue) => {
                      if (newValue) setEndDate(newValue);
                    }}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
          <SortableTable
            rows={forms}
            isLoading={isLoading}
            formListType={formListType}
            selectedFormIds={selectedFormIds}
            setSelectedFormIds={setSelectedFormIds}
            bulkActionInProgress={bulkActionInProgress}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            isFetchingNextPage={isFetchingNextPage}
            setFilterModel={setFilterModel}
          />
        </Box>
      </Box>
      {bulkActionDialog &&
        (() => {
          return (
            <Dialog
              open={!!bulkActionDialog}
              closeDialog={() => setBulkActionDialog(undefined)}
              id="bulk-action-dialog"
              cancelText="Cancel"
              maxWidth={480}
              confirmText={confirmLabel}
              confirmClick={() => handleBulkAction()}
            >
              <Typography marginBottom={1}>
                Are you sure you want to {verb} {noun}?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {warning}
              </Typography>
            </Dialog>
          );
        })()}
    </Box>
  );
}

export default FormList;
